import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="searchable-select"
//
// A reusable, accessible, searchable select: a clickable trigger that opens a
// popover with a search input and a filterable, optionally-grouped option list.
// Backs a hidden form field so it can drop into any Rails form in place of a
// native <select>.
//
// Public surface (callable by composing controllers via Stimulus outlets):
//
//   applySelection(value, label) — set the current selection (no event fired)
//   addOption({ value, label, sublabel, search, kind, group }) — append + select
//   open(), close(), toggle()
//
// Events emitted on the controller element:
//
//   searchable-select:select { value, label, kind } — user picked an option
//   searchable-select:open                          — panel opened
//   searchable-select:close                         — panel closed
//
// Markup contract: see app/views/shared/_searchable_select.html.erb.
export default class extends Controller {
  static targets = [
    "trigger", "display", "hidden", "panel", "search",
    "list", "option", "empty", "groupHeading", "clear"
  ]
  static values = {
    placeholder: { type: String, default: "Select…" }
  }

  connect() {
    this._activeIndex = -1
  }

  // ---- open / close ----

  toggle(event) {
    event?.preventDefault()
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    if (this.isOpen()) return
    this.panelTarget.hidden = false
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.searchTarget.value = ""
    this.filter()
    requestAnimationFrame(() => this.searchTarget.focus())
    this.dispatch("open")
  }

  close() {
    if (!this.isOpen()) return
    this.panelTarget.hidden = true
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this._activeIndex = -1
    this.clearHighlight()
    this.dispatch("close")
  }

  clickAway(event) {
    if (!this.isOpen()) return
    if (this.element.contains(event.target)) return
    this.close()
  }

  isOpen() {
    return !this.panelTarget.hidden
  }

  // ---- filtering ----

  filter() {
    const query = (this.searchTarget.value || "").trim().toLowerCase()
    let visible = 0
    this.optionTargets.forEach((option) => {
      const haystack = option.dataset.search || option.textContent.toLowerCase()
      const match = query.length === 0 || haystack.includes(query)
      option.hidden = !match
      if (match) visible += 1
    })
    this.refreshGroupHeadings()
    if (this.hasEmptyTarget) this.emptyTarget.hidden = visible > 0
    this._activeIndex = -1
    this.clearHighlight()
  }

  refreshGroupHeadings() {
    if (!this.hasGroupHeadingTarget) return
    this.groupHeadingTargets.forEach((heading) => {
      let sibling = heading.nextElementSibling
      let anyVisible = false
      while (sibling && !sibling.matches('[data-searchable-select-target="groupHeading"]')) {
        if (sibling.matches('[data-searchable-select-target="option"]') && !sibling.hidden) {
          anyVisible = true
          break
        }
        sibling = sibling.nextElementSibling
      }
      heading.hidden = !anyVisible
    })
  }

  // ---- selection ----

  choose(event) {
    const option = event.currentTarget
    this.applySelection(option.dataset.value, option.dataset.label)
    this.dispatch("select", {
      detail: { value: option.dataset.value, label: option.dataset.label, kind: option.dataset.kind }
    })
    this.close()
  }

  applySelection(value, label) {
    this.hiddenTarget.value = value || ""
    const text = label || this.placeholderValue
    this.displayTarget.textContent = text
    this.displayTarget.classList.toggle("searchable-select-placeholder", !label)
    this.optionTargets.forEach((option) => {
      option.setAttribute("aria-selected", option.dataset.value === value ? "true" : "false")
    })
    if (this.hasClearTarget) this.clearTarget.hidden = !value
  }

  clear(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.applySelection("", null)
    this.dispatch("clear")
    this.dispatch("select", { detail: { value: "", label: null, kind: null } })
  }

  // ---- dynamic options (used by composing controllers) ----

  addOption({ value, label, sublabel, search, kind, group }) {
    const button = document.createElement("button")
    button.type = "button"
    button.setAttribute("role", "option")
    button.setAttribute("data-searchable-select-target", "option")
    button.setAttribute("data-action", "click->searchable-select#choose mouseenter->searchable-select#highlight")
    button.dataset.value  = value
    button.dataset.label  = label
    button.dataset.search = (search || label || "").toLowerCase()
    if (kind) button.dataset.kind = kind
    button.setAttribute("aria-selected", "true")
    button.classList.add("searchable-select-option")
    button.innerHTML = `<span class="searchable-select-option-name">${this.escape(label)}</span>${sublabel ? `<span class="searchable-select-option-sub">${this.escape(sublabel)}</span>` : ""}`

    if (group) {
      const heading = this.groupHeadingTargets.find((node) => node.dataset.group === group)
      if (heading) {
        heading.insertAdjacentElement("afterend", button)
      } else {
        this.listTarget.insertBefore(button, this.listTarget.firstChild)
      }
    } else {
      this.listTarget.insertBefore(button, this.listTarget.firstChild)
    }
    this.applySelection(value, label)
  }

  // ---- keyboard / hover navigation ----

  keyboard(event) {
    const visible = this.visibleOptions()
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this._activeIndex = Math.min(this._activeIndex + 1, visible.length - 1)
        this.highlightAt(visible)
        break
      case "ArrowUp":
        event.preventDefault()
        this._activeIndex = Math.max(this._activeIndex - 1, 0)
        this.highlightAt(visible)
        break
      case "Enter":
        if (this._activeIndex >= 0 && visible[this._activeIndex]) {
          event.preventDefault()
          visible[this._activeIndex].click()
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        this.triggerTarget.focus()
        break
    }
  }

  highlight(event) {
    this.clearHighlight()
    event.currentTarget.classList.add("searchable-select-option-active")
    this._activeIndex = this.visibleOptions().indexOf(event.currentTarget)
  }

  highlightAt(visible) {
    this.clearHighlight()
    const option = visible[this._activeIndex]
    if (!option) return
    option.classList.add("searchable-select-option-active")
    option.scrollIntoView({ block: "nearest" })
  }

  clearHighlight() {
    this.optionTargets.forEach((option) => option.classList.remove("searchable-select-option-active"))
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.hidden)
  }

  // ---- helpers ----

  escape(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
